import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ActivitySparkline from '../components/charts/ActivitySparkline';
import BarRows from '../components/charts/BarRows';
import StackBar from '../components/charts/StackBar';
import { ErrorBanner } from '../components/Feedback';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Module 12 — the Admin Dashboard. Platform KPIs, drawn entirely from tables the other
 * modules already fill.
 *
 * <p>Four headline numbers lead, because those are what the page is opened for; the
 * breakdowns sit below them. Each panel links to the screen that acts on it — a number
 * a reader cannot follow up on is trivia.
 */
export default function AdminDashboardPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.adminDashboard().then(setData).catch(setError);
  }, []);

  return (
    <>
      <div className="page-head">
        <h1>Dashboard</h1>
        <p>
          Signed in as {user?.roleLabel}. Everything below is platform-wide and live.
        </p>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {!data && !error && <div className="empty">Loading…</div>}

      {data && (
        <>
          <div className="kpi-row">
            <Kpi label="Users" value={data.headline.users}
                 note={`${slice(data.usersByRole, 'TEACHER')} teachers · ${slice(data.usersByRole, 'STUDENT')} students`} />
            <Kpi label="Schools" value={data.headline.schools}
                 note={`${slice(data.schoolsByStatus, 'ACTIVE')} active`} />
            <Kpi label="Books activated" value={data.headline.booksActivated}
                 note={`${data.codes.used.toLocaleString()} codes redeemed`} />
            <Kpi label="Awaiting verification" value={data.headline.pendingVerifications}
                 note={data.headline.pendingVerifications === 0
                   ? 'Queue is clear'
                   : 'Teachers waiting on a decision'} />
          </div>

          <div className="panel-grid">
            <section className="card card-pad panel">
              <h2>Activation codes</h2>
              <p className="panel-sub">
                {data.codes.usedPercentOfIssued}% of the codes issued to schools have been
                redeemed.
              </p>
              <StackBar
                total={data.codes.total}
                segments={[
                  { label: 'Unused', value: data.codes.unused,
                    color: 'var(--chart-1)', ink: 'var(--color-accent-200)' },
                  { label: 'Allocated', value: data.codes.allocated,
                    color: 'var(--chart-2)', ink: '#fff' },
                  { label: 'Used', value: data.codes.used,
                    color: 'var(--chart-3)', ink: '#fff' },
                ]}
              />
              <p className="hint">
                <Link to="/codes">Generate or export codes</Link>
              </p>
            </section>

            <section className="card card-pad panel">
              <h2>Resource activity</h2>
              <p className="panel-sub">
                Resources opened per day over the last two weeks ·{' '}
                {data.opensLast30Days.toLocaleString()} in the last 30 days.
              </p>
              <ActivitySparkline days={data.activity} />
            </section>

            <section className="card card-pad panel">
              <h2>Teacher verification</h2>
              <p className="panel-sub">Where every teacher stands in the Module 2 queue.</p>
              <BarRows slices={data.verificationQueue}
                       emptyMessage="No teachers have registered yet." />
              <p className="hint">
                <Link to="/verification">Open the review queue</Link>
              </p>
            </section>

            <section className="card card-pad panel">
              <h2>Users by role</h2>
              <p className="panel-sub">Every account on the platform.</p>
              <BarRows slices={data.usersByRole} />
              <p className="hint">
                <Link to="/users">Manage internal users</Link>
              </p>
            </section>

            <section className="card card-pad panel">
              <h2>Schools</h2>
              <p className="panel-sub">The School Master, by customer status.</p>
              <BarRows slices={data.schoolsByStatus}
                       emptyMessage="No schools yet — the registration lookup searches these." />
              <p className="hint">
                <Link to="/schools">Manage schools</Link>
              </p>
            </section>

            <section className="card card-pad panel">
              <h2>Student access</h2>
              <p className="panel-sub">
                The 15-month window on each activated book.
              </p>
              <BarRows
                slices={[
                  { key: 'ACTIVE', label: 'Active', value: data.access.active },
                  { key: 'RENEWAL_DUE', label: 'Renewal due', value: data.access.renewalDue },
                  { key: 'EXPIRED', label: 'Expired', value: data.access.expired },
                ]}
                emptyMessage="No books have been activated yet."
              />
            </section>

            <section className="card card-pad panel">
              <h2>Catalogue</h2>
              <p className="panel-sub">What is available to adopt and to teach from.</p>
              <BarRows
                slices={[
                  { key: 'titles', label: 'Titles', value: data.catalogue.titles },
                  { key: 'chapters', label: 'Chapters', value: data.catalogue.chapters },
                  { key: 'resources', label: 'Resources', value: data.catalogue.resources },
                  { key: 'adoptions', label: 'Adoptions', value: data.catalogue.adoptions },
                ]}
                emptyMessage="Nothing in the catalogue yet."
              />
              <p className="hint">
                <Link to="/content">Manage content</Link>
              </p>
            </section>
          </div>

          <p className="tiny muted" style={{ marginTop: 'var(--s2)' }}>
            Counted at {new Date(data.generatedAt).toLocaleTimeString()}. Dates are UTC.
          </p>
        </>
      )}
    </>
  );
}

function Kpi({ label, value, note }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value.toLocaleString()}</div>
      {note && <div className="kpi-note">{note}</div>}
    </div>
  );
}

function slice(slices, key) {
  return (slices.find((entry) => entry.key === key)?.value ?? 0).toLocaleString();
}
